import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { client } from '../generated/client.gen';

// ---------- Shared types ----------

export type ConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'starts_with'
  | 'ends_with'
  | 'in'
  | 'not_in'
  | 'gt'
  | 'lt'
  | 'between'
  | 'before'
  | 'after'
  | 'is_set'
  | 'is_empty';

export interface Condition {
  field: string;
  operator: ConditionOperator;
  value?: string | number | boolean | string[] | null;
}

export interface ConditionTree {
  conjunction: 'and' | 'or';
  conditions: Condition[];
}

// ---------- Tags ----------

export interface Tag {
  id: string;
  name: string;
  color: string;
  namespace?: string;
  autoRule?: ConditionTree;
  createdBy?: string;
  createdAt?: string;
  lastUsedAt?: string;
  contactCount: number;
}

export interface CreateTagRequest {
  name: string;
  color: string;
  namespace?: string;
  autoRule?: ConditionTree;
}

const tagsQueryKey = ['workspace', 'tags'] as const;

function useTags() {
  return useQuery({
    queryKey: tagsQueryKey,
    queryFn: async (): Promise<Tag[]> => {
      try {
        const { data } = await client.instance.get<{ data?: Tag[] } | Tag[]>('/tags');
        if (Array.isArray(data)) return data;
        return data.data ?? [];
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 404) return [];
        throw e;
      }
    },
  });
}

function useCreateTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateTagRequest): Promise<Tag> => {
      const { data } = await client.instance.post<Tag>('/tags', body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tagsQueryKey }),
  });
}

function useUpdateTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Partial<CreateTagRequest> }): Promise<Tag> => {
      const { data } = await client.instance.put<Tag>(`/tags/${id}`, body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tagsQueryKey }),
  });
}

function useDeleteTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.instance.delete(`/tags/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tagsQueryKey }),
  });
}

export const tagsApi = {
  useList: useTags,
  useCreate: useCreateTag,
  useUpdate: useUpdateTag,
  useDelete: useDeleteTag,
};

// ---------- Custom fields ----------

export interface CustomFieldDef {
  id: string;
  key?: string;
  section: string;
  name: string;
  type: string;
  required?: boolean;
  options?: string[];
  source?: string;
  createdBy?: string;
  createdAt?: string;
}

const customFieldsQueryKey = ['workspace', 'customFields'] as const;

function useCustomFields() {
  return useQuery({
    queryKey: customFieldsQueryKey,
    queryFn: async (): Promise<CustomFieldDef[]> => {
      try {
        const { data } = await client.instance.get<{ data?: CustomFieldDef[] } | CustomFieldDef[]>(
          '/custom-fields',
        );
        if (Array.isArray(data)) return data;
        return data.data ?? [];
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 404) return [];
        throw e;
      }
    },
  });
}

function useDeleteCustomField() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.instance.delete(`/custom-fields/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: customFieldsQueryKey }),
  });
}

export const customFieldsApi = {
  useList: useCustomFields,
  useDelete: useDeleteCustomField,
};

// ---------- Segments ----------

export interface SegmentSyncRef {
  integrationId: string;
  externalListId?: string;
  lastSyncAt?: string;
  lastSyncCount?: number;
  lastSyncStatus?: 'succeeded' | 'failed' | (string & {});
  lastSyncError?: string;
  activeJobId?: string;
  autoSync: boolean;
  linkedAt?: string;
  linkedBy?: string;
  forwardWatermark?: string;
  lastFullSyncAt?: string;
  fullSyncRequestedAt?: string;
  lastSyncMode?: 'full' | 'incremental';
  consecutiveFailures: number;
  autoSyncPausedReason?: string;
}

export interface Segment {
  id: string;
  name: string;
  description?: string;
  color: string;
  conditions: ConditionTree;
  ownerId?: string;
  createdAt?: string;
  updatedAt?: string;
  rulesUpdatedAt?: string;
  contactCount: number;
  integrationSyncRefs?: Record<string, SegmentSyncRef>;
}

export interface CreateSegmentRequest {
  name: string;
  description?: string;
  color: string;
  conditions: ConditionTree;
}

export const segmentsQueryKey = ['workspace', 'segments'] as const;

function useSegments() {
  return useQuery({
    queryKey: segmentsQueryKey,
    queryFn: async (): Promise<Segment[]> => {
      try {
        const { data } = await client.instance.get<{ data?: Segment[] } | Segment[]>('/segments');
        if (Array.isArray(data)) return data;
        return data.data ?? [];
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 404) return [];
        throw e;
      }
    },
  });
}

function useCreateSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateSegmentRequest): Promise<Segment> => {
      const { data } = await client.instance.post<Segment>('/segments', body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: segmentsQueryKey }),
  });
}

function useUpdateSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: Partial<CreateSegmentRequest> }): Promise<Segment> => {
      const { data } = await client.instance.put<Segment>(`/segments/${id}`, body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: segmentsQueryKey }),
  });
}

function useDeleteSegment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.instance.delete(`/segments/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: segmentsQueryKey }),
  });
}

export const segmentsApi = {
  useList: useSegments,
  useCreate: useCreateSegment,
  useUpdate: useUpdateSegment,
  useDelete: useDeleteSegment,
};

function replaceCachedSegment(old: Segment[] | undefined, segment: Segment): Segment[] | undefined {
  return old?.map((s) => (s.id === segment.id ? segment : s));
}

function useUpsertSegmentLink(segmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      integrationId,
      autoSync,
    }: {
      integrationId: string;
      autoSync: boolean;
    }): Promise<Segment> => {
      const { data } = await client.instance.put<Segment>(
        `/segments/${segmentId}/links/${integrationId}`,
        { autoSync },
      );
      return data;
    },
    onSuccess: (segment) => {
      if (segment?.id) {
        queryClient.setQueryData<Segment[]>(segmentsQueryKey, (old) =>
          replaceCachedSegment(old, segment),
        );
      }
      return queryClient.invalidateQueries({ queryKey: segmentsQueryKey });
    },
  });
}

function useUnlinkSegment(segmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (integrationId: string) => {
      await client.instance.delete(`/segments/${segmentId}/links/${integrationId}`);
    },
    onSuccess: (_data, integrationId) => {
      queryClient.setQueryData<Segment[]>(segmentsQueryKey, (old) =>
        old?.map((s) => {
          if (s.id !== segmentId || !s.integrationSyncRefs) return s;
          const refs = { ...s.integrationSyncRefs };
          delete refs[integrationId];
          return { ...s, integrationSyncRefs: refs };
        }),
      );
      return queryClient.invalidateQueries({ queryKey: segmentsQueryKey });
    },
  });
}

function useResyncSegmentLink(segmentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (integrationId: string): Promise<Segment> => {
      const { data } = await client.instance.post<Segment>(
        `/segments/${segmentId}/links/${integrationId}/resync`,
      );
      return data;
    },
    onSuccess: (segment) => {
      if (segment?.id) {
        queryClient.setQueryData<Segment[]>(segmentsQueryKey, (old) =>
          replaceCachedSegment(old, segment),
        );
      }
      return queryClient.invalidateQueries({ queryKey: segmentsQueryKey });
    },
  });
}

export const segmentLinksApi = {
  useUpsertLink: useUpsertSegmentLink,
  useUnlink: useUnlinkSegment,
  useResync: useResyncSegmentLink,
};

// ---------- Saved views ----------

export interface SavedView {
  id: string;
  name: string;
  color?: string;
  filters: Record<string, unknown>;
  sort?: { field: string; direction: 'asc' | 'desc' };
  columns?: string[];
  ownerId?: string;
  scope: 'user' | 'workspace';
  contactCount?: number;
}

export interface CreateSavedViewRequest {
  name: string;
  color?: string;
  filters: Record<string, unknown>;
  sort?: { field: string; direction: 'asc' | 'desc' };
  columns?: string[];
  scope?: 'user' | 'workspace';
}

const savedViewsQueryKey = ['workspace', 'saved-views'] as const;

function useSavedViews() {
  return useQuery({
    queryKey: savedViewsQueryKey,
    queryFn: async (): Promise<SavedView[]> => {
      try {
        const { data } = await client.instance.get<{ data?: SavedView[] } | SavedView[]>(
          '/saved-views',
        );
        if (Array.isArray(data)) return data;
        return data.data ?? [];
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 404) return [];
        throw e;
      }
    },
    // Keep saved views fresh across tab switches without spamming the server.
    staleTime: 30 * 1000,
  });
}

function useCreateSavedView() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateSavedViewRequest): Promise<SavedView> => {
      const { data } = await client.instance.post<SavedView>('/saved-views', body);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: savedViewsQueryKey }),
  });
}

function useDeleteSavedView() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.instance.delete(`/saved-views/${id}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: savedViewsQueryKey }),
  });
}

export const savedViewsApi = {
  useList: useSavedViews,
  useCreate: useCreateSavedView,
  useDelete: useDeleteSavedView,
};

// ---------- Audit stats ----------

export interface AuditStats {
  today: number;
  last7Days: number;
  deletionsThisWeek: number;
  syncEvents: number;
}

const auditStatsQueryKey = ['audit', 'stats'] as const;

function useAuditStats() {
  return useQuery({
    queryKey: auditStatsQueryKey,
    queryFn: async (): Promise<AuditStats | null> => {
      try {
        const { data } = await client.instance.get<AuditStats>('/audit/stats');
        return data;
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 404) return null;
        throw e;
      }
    },
    staleTime: 60 * 1000,
  });
}

export const auditApi = {
  useStats: useAuditStats,
};

// ---------- Contact restore ----------

function useRestoreContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, version }: { id: string; version: number }) => {
      const { data } = await client.instance.post(
        `/contacts/${id}/restore?version=${version}`,
      );
      return data;
    },
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['contacts', 'detail', id] });
      queryClient.invalidateQueries({ queryKey: ['contacts', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['auditLogs'] });
    },
  });
}

export const contactRestoreApi = {
  useRestore: useRestoreContact,
};
